import { Archive, Search } from "lucide-react";
import { Button } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { ToggleChip } from "./shared/toolbar/ToggleChip";
import { ClearFilters } from "./shared/toolbar/ClearFilters";

export interface TemplateFilters {
  search: string;
  showArchived: boolean;
}

interface TemplatesToolbarProps {
  filters: TemplateFilters;
  onFilterChange: <K extends keyof TemplateFilters>(key: K, value: TemplateFilters[K]) => void;
  onNewTemplate: () => void;
  canCreate?: boolean;
  archivedCount?: number;
}

export function TemplatesToolbar({
  filters,
  onFilterChange,
  onNewTemplate,
  canCreate = true,
  archivedCount = 0,
}: TemplatesToolbarProps) {
  const { t } = useTranslation();
  const tb = (key: string, defaultValue: string) => t(`iam.templates.${key}`, defaultValue);

  const activeFilterCount = [filters.showArchived].filter(Boolean).length;

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 px-3 py-3 sm:px-6">
      <label className="relative order-1 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:flex-none">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          type="search"
          placeholder={tb("searchPlaceholder", "Search roles...")}
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70 sm:w-52 sm:flex-none"
        />
      </label>

      <div className="order-3 flex w-full flex-wrap items-center gap-1.5 sm:order-2 sm:w-auto sm:flex-1">
        <ToggleChip
          icon={<Archive className="h-3 w-3 shrink-0" />}
          label={tb("showArchived", "Show archived")}
          active={filters.showArchived}
          count={archivedCount > 0 ? archivedCount : undefined}
          title={filters.showArchived
            ? tb("hideArchivedTooltip", "Hide archived roles")
            : tb("showArchivedTooltip", "Show archived roles")}
          onClick={() => onFilterChange("showArchived", !filters.showArchived)}
        />
        {activeFilterCount > 0 && (
          <ClearFilters
            label={tb("clearFilters", "Clear filters")}
            onClick={() => onFilterChange("showArchived", false)}
          />
        )}
      </div>

      {canCreate && (
        <Button size="sm" className="order-2 ml-auto sm:order-3" iconStart="plus" onClick={onNewTemplate}>
          {tb("newTemplate", "New role")}
        </Button>
      )}
    </div>
  );
}
